import {
    ContentFile,
    PublicationForm,
    PublicationType,
    VisibilityType,
    VisibilityTypeOption,
} from '@web/vin-sessions/types';
import { useCallback, useEffect, useState } from 'react';
import useTranslationData from './use-translation-data';
import { isRichTextEmpty } from '../description.utils';
interface Props {
    setPublicationInformation: (nextState: Partial<PublicationForm>) => void;
}

export interface State {
    publicationTypeErrorMessage: string;
    titleErrorMessage: string;
    docCodeErrorMessage: string;
    visibilityErrorMessage: string;
    contentLinkErrorMessage: string;
    contentFileErrorMessage: string;
    descriptionErrorMessage: string;
    publishedYearErrorMessage: string;
    publishedMonthErrorMessage: string;
}

const usePublicationForm = ({ setPublicationInformation }: Props) => {
    const [state, _setState] = useState<State>({
        titleErrorMessage: '',
        publicationTypeErrorMessage: '',
        docCodeErrorMessage: '',
        visibilityErrorMessage: '',
        contentLinkErrorMessage: '',
        contentFileErrorMessage: '',
        descriptionErrorMessage: '',
        publishedYearErrorMessage: '',
        publishedMonthErrorMessage: '',
    });

    const [isStatusTypeLoading, setIsStatusTypeLoading] = useState(false);
    const { validationMessageDataTranslate } = useTranslationData();

    const errors = {
        title: validationMessageDataTranslate.title,
        publicationType: validationMessageDataTranslate.publicationType,
        docCode: validationMessageDataTranslate.docCode,
        visibilityType: validationMessageDataTranslate.visibilityType,
        contentLink: validationMessageDataTranslate.contentURL,
        description: validationMessageDataTranslate.description,
        contentFile: validationMessageDataTranslate.contentFile,
        contentFileZip: validationMessageDataTranslate.contentFileZip,
        publishedYear: validationMessageDataTranslate.publishedYear,
        publishedMonth: validationMessageDataTranslate.publishedMonth,
    };

    const setState = useCallback((nextState: Partial<State>) => {
        _setState((prevState) => ({ ...prevState, ...nextState }));
    }, []);

    const handleTitleChange = useCallback(
        (title: string) => {
            setPublicationInformation({ title: title.length > 0 ? title : 'Untitled' });
            if (!title || title == '' || title == 'Untitled') {
                setState({ titleErrorMessage: errors.title });
                return;
            }
            setState({ titleErrorMessage: '' });
        },
        [errors.title, setState, setPublicationInformation]
    );

    const handlePublicationTypeChange = useCallback(
        (publicationType: PublicationType, contentFile?: ContentFile) => {
            setPublicationInformation({
                publicationType: publicationType,
                docTypeId: publicationType && publicationType.id,
            });
            if (!publicationType || (publicationType && (publicationType.id == '' || publicationType.code == ''))) {
                setState({ publicationTypeErrorMessage: errors.publicationType });
                return;
            }
            setState({ publicationTypeErrorMessage: '' });

            if (contentFile && contentFile.fileName) {
                const isZip = contentFile.fileName.toLowerCase().endsWith('.zip') || contentFile.fileType === 'ZIP';
                const isServiceDiagnostics = publicationType.label === 'Service Diagnostics';

                if (isZip && !isServiceDiagnostics) {
                    setState({ contentFileErrorMessage: errors.contentFileZip });
                } else {
                    setState({ contentFileErrorMessage: '' });
                }
            }
        },
        [errors.publicationType, errors.contentFileZip, setState, setPublicationInformation]
    );

    const handleChangeStatusType = useCallback(
        (status: string) => {
            setPublicationInformation({ status: status });
        },
        [setPublicationInformation]
    );

    const handleDocumentCodeChange = useCallback(
        (docCode: string) => {
            setPublicationInformation({ docCode: docCode });
            if (docCode === '' || docCode === null) {
                setState({ docCodeErrorMessage: errors.docCode });
                return;
            }
            setState({ docCodeErrorMessage: '' });
        },
        [errors.docCode, setState, setPublicationInformation]
    );

    const handleCreatedDateChange = useCallback(
        (date: Date | null) => {
            setPublicationInformation({ createdDate: date });
        },
        [setPublicationInformation]
    );

    const handleAddKeywords = useCallback(
        (keywords: string[]) => {
            setPublicationInformation({ keywords: keywords });
        },
        [setPublicationInformation]
    );

    const handleAddToolNumbers = useCallback(
        (toolNumbers: string[]) => {
            setPublicationInformation({ toolNumbers: toolNumbers });
        },
        [setPublicationInformation]
    );

    const handleAddDtcCodes = useCallback(
        (dtcCodes: string[]) => {
            setPublicationInformation({ dtcCodes: dtcCodes });
        },
        [setPublicationInformation]
    );

    const handleDescriptionChange = useCallback(
        (description: string) => {
            setPublicationInformation({ description: description });
            if (isRichTextEmpty(description)) {
                setState({ descriptionErrorMessage: errors.description });
                return;
            }
            setState({ descriptionErrorMessage: '' });
        },
        [errors.description, setState, setPublicationInformation]
    );

    const handleVisibilityTypeChange = useCallback(
        (visibilityType: VisibilityTypeOption) => {
            setPublicationInformation({ visibilityLevel: visibilityType });
            if (!visibilityType || (visibilityType && (visibilityType.label == '' || visibilityType.value == ''))) {
                setState({ visibilityErrorMessage: errors.visibilityType });
                return;
            }
            setState({ visibilityErrorMessage: '' });
        },
        [errors.visibilityType, setState, setPublicationInformation]
    );

    const handleContentFileChange = useCallback(
        (
            contentFile:
                | {
                      fileName: string;
                      fileType: string;
                      createdDate: string;
                      createdTimestamp: string;
                      createdBy?: string;
                  }
                | undefined
        ) => {
            setPublicationInformation({ contentFile: contentFile });
            if (!contentFile || (contentFile && (contentFile.fileName == '' || contentFile.fileType == ''))) {
                setState({ contentFileErrorMessage: errors.contentFile });
                return;
            }
            setState({ contentFileErrorMessage: '' });
        },
        [errors.contentFile, setState, setPublicationInformation]
    );

    const handleContentURLChange = useCallback(
        (contentURL: string) => {
            setPublicationInformation({ url: contentURL });
            if (contentURL == '' || contentURL == null) {
                setState({ contentLinkErrorMessage: errors.contentLink });
                return;
            }
            setState({ contentLinkErrorMessage: '' });
        },
        [errors.contentLink, setState, setPublicationInformation]
    );

    const handleFileUploadFlag = useCallback(
        (fileUploadChange: boolean) => {
            setPublicationInformation({
                fileUploadChange: fileUploadChange,
            });
        },
        [setPublicationInformation]
    );

    const setCreatedAccount = useCallback(
        (accountId: string, name: string, firstName: string, lastName: string, timeZone: string, locale: string) => {
            setPublicationInformation({
                createdAccount: {
                    accountId: accountId,
                    name: name,
                    firstName: firstName,
                    lastName: lastName,
                    timeZone: timeZone,
                    locale: locale,
                },
            });
        },
        [setPublicationInformation]
    );

    const setCreatedTime = useCallback(
        (createdTime: string) => {
            setPublicationInformation({ createdTimestamp: createdTime });
        },
        [setPublicationInformation]
    );

    const handlePublishedYearChange = useCallback(
        (publishedYear?: number) => {
            setPublicationInformation({ publishedYear });
            setState({ publishedYearErrorMessage: publishedYear ? '' : errors.publishedYear });
        },
        [errors.publishedYear, setState, setPublicationInformation]
    );

    const handlePublishedMonthChange = useCallback(
        (publishedMonth?: number) => {
            setPublicationInformation({ publishedMonth });
            setState({ publishedMonthErrorMessage: publishedMonth ? '' : errors.publishedMonth });
        },
        [errors.publishedMonth, setState, setPublicationInformation]
    );

    const handlePubVersionChange = useCallback(
        (pubVersion: number) => {
            setPublicationInformation({ pubVersion });
        },
        [setPublicationInformation]
    );

    const handleInteractiveIndChange = useCallback(
        (interactiveInd: boolean) => {
            setPublicationInformation({ interactiveInd });
        },
        [setPublicationInformation]
    );

    const handleTagsChange = useCallback(
        (tagsChange: boolean) => {
            setPublicationInformation({ tagsChange: tagsChange });
        },
        [setPublicationInformation]
    );

    return {
        state,
        errors,
        setState,
        actions: {
            handleAddKeywords,
            handleAddDtcCodes,
            handleAddToolNumbers,
            handleDescriptionChange,
            handleTitleChange,
            handleChangeStatusType,
            handlePublicationTypeChange,
            handleDocumentCodeChange,
            handleVisibilityTypeChange,
            handleContentURLChange,
            handleCreatedDateChange,
            handleContentFileChange,
            handleFileUploadFlag,
            handleTagsChange,
            handlePublishedYearChange,
            handlePublishedMonthChange,
            handleInteractiveIndChange,
            handlePubVersionChange,
            setCreatedAccount,
            setCreatedTime,
            isStatusTypeLoading,
        },
    };
};

export type UsePublicationFormReturnType = ReturnType<typeof usePublicationForm>;

export default usePublicationForm;
